import { ActionIcon, Menu } from '@mantine/core';
import { IconDownload, IconTrash } from '@tabler/icons-react';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import TreeNodeOptions from '@/components/shared/TreeNodeOptions';
import FieldCheckbox from '@/components/shared/form/FieldCheckbox';
import Loader from '@/components/ui/Loader';
import { getDocumentLockMessage } from '@/entities/Document/documentContentLock';
import {
  TREE_ROOT_NODE_ID,
  TreeNodeFile,
  canDownloadTreeDocument,
  getDocumentAvailabilityIconReason,
  isInsidePersonalWorkspace,
} from '@/entities/Tree';
import { useLocalStore } from '@/hooks/useLocalStore';
import { DocumentActionsStore } from '@/stores/local/shared/DocumentActionsStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { useDevices } from '@/utils/devices';

import s from './FileActions.module.scss';

type Props = {
  deletePopupStore: PopupStore<string>;
  model: TreeNodeFile;
  nodeStore: TreeStore;
  withSelection?: boolean;
  onAfterToggle?: () => void;
};

const FileActions: React.FC<Props> = ({ deletePopupStore, model, nodeStore, withSelection, onAfterToggle }) => {
  const { isCompactViewport } = useDevices();

  const { documentId, id } = useParams();
  const { treeStore, permissionsStore, selectionStore } = useSidebarDocumentsStore();

  const documentActionStore = useLocalStore(
    () => new DocumentActionsStore({ documentId, organizationId: id, treeStore }),
  );

  const inPersonalWorkspace = isInsidePersonalWorkspace(nodeStore);
  const canDelete =
    model.id !== TREE_ROOT_NODE_ID && (inPersonalWorkspace || permissionsStore.canDelete(model.id, 'document'));
  const canDownload = canDownloadTreeDocument(model);
  const canDeleteWhenUnlocked = canDelete && model.contentAvailable;
  const hasDocumentActions = canDownload || canDelete;
  const isMenuDisabled = permissionsStore.meta.isLoading || !hasDocumentActions;
  const lockHint =
    !model.contentAvailable && !canDownload ? getDocumentLockMessage(getDocumentAvailabilityIconReason(model)) : null;
  const menuTooltip = isMenuDisabled
    ? permissionsStore.meta.isLoading
      ? 'Загружаю права доступа...'
      : (lockHint ?? 'Недостаточно прав для действий с этим документом')
    : undefined;

  const handleStopPropagation = React.useCallback((event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleDownload = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      documentActionStore.setDocumentId(model.id);

      documentActionStore.downloadDocument();
    },
    [documentActionStore, model],
  );

  const handleDelete = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      deletePopupStore.open(model.id);
    },
    [deletePopupStore, model],
  );

  const isDisabled = documentActionStore.downloadMeta.isLoading || documentActionStore.deleteMeta.isLoading;

  const checkboxState = withSelection ? selectionStore.getNodeState(nodeStore) : null;
  const selectionCheckboxDisabled = !model.contentAvailable;

  const handleToggleSelection = React.useCallback(() => {
    if (!model.contentAvailable) {
      return;
    }

    selectionStore.toggleNode(nodeStore);
    onAfterToggle?.();
  }, [model.contentAvailable, selectionStore, nodeStore, onAfterToggle]);

  const rootClassName = cn(s.root, withSelection ? s['root_three-columns'] : s['root_two-columns']);

  if (isCompactViewport) {
    return null;
  }

  return (
    <div className={rootClassName}>
      <div className={s.root__slot}>
        {documentActionStore.downloadMeta.isLoading || documentActionStore.deleteMeta.isLoading ? (
          <Loader type="oval" size="xs" gap="sm" />
        ) : (
          <ActionIcon
            className={cn(s.root__download, isDisabled && s.root__download_disabled)}
            disabled={isDisabled || !canDownload}
            variant="subtle"
            size="sm"
            onClick={handleDownload}
            title={!canDownload ? (lockHint ?? undefined) : undefined}
          >
            <IconDownload size={16} />
          </ActionIcon>
        )}
      </div>
      <div className={s.root__slot}>
        <TreeNodeOptions
          disabled={isMenuDisabled}
          disabledTooltip={menuTooltip}
          items={
            <>
              <Menu.Item
                disabled={!canDeleteWhenUnlocked}
                onClick={handleDelete}
                leftSection={<IconTrash className={s.root__remove} size={16} />}
              >
                Удалить
              </Menu.Item>
            </>
          }
        />
      </div>
      {withSelection ? (
        <div className={cn(s.root__slot, s.root__slot_checkbox)}>
          {checkboxState ? (
            <FieldCheckbox
              checked={checkboxState.checked}
              indeterminate={checkboxState.indeterminate}
              onToggle={handleToggleSelection}
              onClick={handleStopPropagation}
              disabled={selectionCheckboxDisabled}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default observer(FileActions);
